import { useState, useRef, useEffect } from "react";
import { Button } from "@mui/material";
import { clearStoredOtpSessionData, getOtpRemainingSeconds, verifyOtp } from "../../services/otp";
import { emitSnackbar } from "../../lib/snackbar-emitter";
import manageErrors from "../../lib/error-management";
import { requestSession } from "./Step1AdminData";
import { completeKwikLogin } from "../../services/auth";
import { useNavigate } from "react-router-dom";

interface sessionData {
  session_id: string | null,
  expiration_timeout: number,
  retry_timeout: number,
  requested_at_ms?: number,
}

interface Step2Props {
  onNext: () => void;
  onBack: () => void;
  data: sessionData;
  login: undefined | boolean
}


const OTP_CODE_LENGTH = 6;

const createEmptyCode = () => new Array(OTP_CODE_LENGTH).fill("");

const timeString = (time: number) => {
  const minutes = Math.floor(time / 60);
  const seconds = time % 60;
  return <>{minutes}:{seconds.toString().padStart(2, "0")}</>
}

const Step2ValidateToken = ({ onNext, onBack, data, login }: Step2Props) => {
  const navigate = useNavigate();
  const [code, setCode] = useState<string[]>(() => createEmptyCode());
  const [timeLeft, setTimeLeft] = useState(() => getOtpRemainingSeconds(data, 'expiration_timeout'));
  const [retryTime, setRetryTime] = useState(() => getOtpRemainingSeconds(data, 'retry_timeout'));
  const [loading, setLoading] = useState(false)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const isFilled = code.every((d) => d !== "");

  useEffect(() => {
    setTimeLeft(getOtpRemainingSeconds(data, 'expiration_timeout'));
    setRetryTime(getOtpRemainingSeconds(data, 'retry_timeout'));
  }, [data]);

  useEffect(() => {
    if (timeLeft <= 0) return;
    const timer = setInterval(() => {setTimeLeft((t) => t - 1); setRetryTime((t) => t - 1) }, 1000);
    return () => clearInterval(timer);
  }, [timeLeft]);

  const handleChange = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    if (value && index < OTP_CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_CODE_LENGTH);
    const newCode = [...code];
    for (let i = 0; i < pasted.length; i++) {
      newCode[i] = pasted[i];
    }
    setCode(newCode);
    const focusIndex = Math.min(pasted.length, OTP_CODE_LENGTH - 1);
    inputRefs.current[focusIndex]?.focus();
  };

  const validateCode = async () => {
    setLoading(true)
    const response = await verifyOtp(data.session_id, code.join(''), login)
    setLoading(false)
    if (response.status === 200){
      clearStoredOtpSessionData()
      if (login){
        const loginResult = await completeKwikLogin(response.data)
        if (!loginResult.ok) {
          emitSnackbar({message: loginResult.error || 'Não foi possível concluir a autenticação.', severity: 'error'})
          setLoading(false)
          return
        }
        navigate('/home')
      } else 
        onNext()
    }
    else
      emitSnackbar({message: manageErrors(response.data), severity: 'error'})
  }

  return (
    <div className="w-full max-w-lg mx-auto">
      <div className="text-center mb-8">
        <h1 className="text-xl font-bold text-foreground mb-2">
          Confirme o código enviado para seu WhatsApp
        </h1>
        <p className="text-sm text-muted-foreground">
          Enviamos um código de 6 dígitos para o número informado
        </p>
      </div>

      <div className="bg-card rounded-lg border border-border shadow-sm p-5 space-y-6">
        <div className="flex justify-center gap-2" onPaste={handlePaste}>
          {code.map((digit, i) => (
            <input
              key={i}
              ref={(el) => { inputRefs.current[i] = el; }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              className="w-10 h-14 text-center text-xl font-semibold border border-border rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all"
            />
          ))}
        </div>

        <p className="text-center text-xs text-muted-foreground">
          O código expira em {timeString(timeLeft)} minutos.
        </p>

        
        <p className="text-center">
          {retryTime <= 0 ? 
          <button type="button" className="text-sm text-accent hover:underline" 
            onClick={() => { setTimeLeft(300); setCode(createEmptyCode()); requestSession() }}>
            Não recebeu o código? Reenviar.
          </button>
          :
          <span className="text-sm text-accent hover:underline">
            Não recebeu o código? Aguarde {timeString(Math.max(retryTime, 0))} minutos para reenviar.
          </span>
        }
        </p>

        <Button
          className="w-full"
          size="large"
          variant='contained'
          disabled={!isFilled || loading}
          onClick={validateCode}
        >
          {loading ? 'Aguarde':'Validar código'}
        </Button>

        <p className="text-center">
          <button onClick={onBack} className="text-sm text-muted-foreground hover:text-foreground underline transition-colors">
            Voltar e corrigir número
          </button>
        </p>
      </div>
    </div>
  );
};

export default Step2ValidateToken;
